import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DocumentFolderTiles } from "@/components/ui/document-folder-tiles";
import { Upload } from "lucide-react";
import { DocumentUpload } from "@/components/ui/document-upload";
import { DocumentList } from "@/components/ui/document-list";
import { useDocuments } from "@/hooks/useDocuments";
import { useToast } from "@/hooks/use-toast";
import { useDocumentFolders } from "@/hooks/useDocumentFolders";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ROOT_FOLDER_VALUE = "root";

export function DocumentsTab() {
  const { toast } = useToast();
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [isSavingFolder, setIsSavingFolder] = useState(false);

  const {
    documents,
    loading: documentsLoading,
    error: documentsError,
    refreshDocuments,
  } = useDocuments({
    autoRefresh: true,
    folderId: selectedFolderId === null ? undefined : selectedFolderId,
    unfiled: selectedFolderId === null,
  });
  const { folders, createFolder, refreshFolders } = useDocumentFolders({
    ownerType: "agency",
  });

  // Fetch ALL documents (no folder filter) to calculate counts client-side
  const {
    documents: allDocuments,
    loading: allDocumentsLoading,
  } = useDocuments({
    autoRefresh: true,
    ownerType: "agency",
    // No folderId filter - get all documents
  });

  // Calculate folder counts from allDocuments
  const foldersWithCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    
    // Count documents per folder
    allDocuments.forEach((doc: any) => {
      const folderId = doc.folderId;
      if (folderId !== null && folderId !== undefined) {
        counts[folderId] = (counts[folderId] || 0) + 1;
      }
    });

    return folders.map((folder) => ({
      ...folder,
      documentCount: counts[folder.id] || 0,
    }));
  }, [folders, allDocuments]);

  const moveToFolder = async (documentId: number, folderId: number | null) => {
    const res = await fetch(`/api/documents/${documentId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ folderId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || "Failed to move");
    }
    await refreshDocuments();
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || isSavingFolder) return;
    try {
      setIsSavingFolder(true);
      await createFolder(newFolderName.trim());
      await refreshFolders();
      toast({ title: "Folder created", variant: "success" });
      setIsCreateFolderOpen(false);
      setNewFolderName("");
    } catch (e: any) {
      toast({
        title: "Create failed",
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setIsSavingFolder(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Agency Documents</h2>
        <DocumentUpload
          trigger={
            <Button>
              <Upload className="h-4 w-4 mr-2" />
              Upload Document
            </Button>
          }
          onUploadSuccess={async () => {
            await refreshDocuments();
          }}
          ownerType="agency"
          folderId={
            selectedFolderId && selectedFolderId > 0
              ? selectedFolderId
              : undefined
          }
        />
      </div>

      <Card>
        <div className="p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <Label className="text-sm">Folder</Label>
            <Select
              value={
                selectedFolderId === null
                  ? ROOT_FOLDER_VALUE
                  : String(selectedFolderId)
              }
              onValueChange={(v) => {
                if (v === ROOT_FOLDER_VALUE) setSelectedFolderId(null);
                else setSelectedFolderId(parseInt(v, 10));
              }}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Root folder" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ROOT_FOLDER_VALUE}>Root folder</SelectItem>
                {folders.map((f) => (
                  <SelectItem key={f.id} value={String(f.id)}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsCreateFolderOpen(true)}
            >
              New Folder
            </Button>
          </div>
        </div>
      </Card>

      <DocumentFolderTiles
        folders={foldersWithCounts}
        onOpenFolder={(f) => {
          setSelectedFolderId(f.id);
        }}
      />

      {documentsError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800 text-sm">
            Error loading documents: {documentsError}
          </p>
        </div>
      )}

      {documentsLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <DocumentList
          documents={documents as any}
          onDocumentUpdate={refreshDocuments}
          showActions={true}
          folders={folders}
          onMoveToFolder={moveToFolder}
          emptyStateAction={
            <DocumentUpload
              trigger={
                <Button>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
              }
              onUploadSuccess={async () => {
                await refreshDocuments();
              }}
              ownerType="agency"
              folderId={
                selectedFolderId && selectedFolderId > 0
                  ? selectedFolderId
                  : undefined
              }
            />
          }
        />
      )}

      {/* Document Upload Dialog */}
      <Dialog
        open={isCreateFolderOpen}
        onOpenChange={(open) => {
          setIsCreateFolderOpen(open);
          if (!open) {
            setNewFolderName("");
            setIsSavingFolder(false);
          }
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Create Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="agency-new-folder-name">Folder name</Label>
            <Input
              id="agency-new-folder-name"
              placeholder="e.g. Contracts"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              autoFocus
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsCreateFolderOpen(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim() || isSavingFolder}
            >
              {isSavingFolder ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


    </div>
  );
}
